import { Injectable, signal } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, tap } from 'rxjs';
import { LoginRequest, LoginResponse, RefreshTokenRequest, RefreshTokenResponse } from '../models/auth.models';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly API_URL = `${environment.apiUrl}/es/Auth/Login`;
  private readonly REFRESH_TOKEN_URL = `${environment.apiUrl}/es/Auth/RefreshToken`;
  private readonly STORAGE_KEY_USER = 'tasafy_user';
  private readonly STORAGE_KEY_TOKEN = 'tasafy_token';
  private readonly STORAGE_KEY_REFRESH_TOKEN = 'tasafy_refresh_token';

  // Signal to hold the current user data
  public currentUser = signal<LoginResponse | null>(null);
  public isAuthenticated = signal<boolean>(false);

  constructor(private http: HttpClient) {
    // Load user from localStorage on service initialization
    this.loadUserFromStorage();
  }

  /**
   * Perform login with username and password
   */
  login(username: string, password: string): Observable<LoginResponse> {
    const loginRequest: LoginRequest = { username, password };
    
    const headers = new HttpHeaders({
      'accept': 'text/plain',
      'x-loginplatform': '0',
      'Content-Type': 'application/json'
    });

    return this.http.post<LoginResponse>(this.API_URL, loginRequest, { headers }).pipe(
      tap(response => {
        this.saveSession(response);
      })
    );
  }

  /**
   * Refresh the access token using the refresh token
   */
  refreshToken(): Observable<LoginResponse> {
    const accessToken = this.getAccessToken();
    const refreshToken = this.getRefreshToken();

    if (!accessToken || !refreshToken) {
      throw new Error('No tokens available for refresh');
    }

    const refreshRequest: RefreshTokenRequest = {
      accessToken,
      refreshToken
    };

    const headers = new HttpHeaders({
      'accept': 'text/plain',
      'x-loginplatform': '0',
      'Content-Type': 'application/json'
    });

    return this.http.post<LoginResponse>(this.REFRESH_TOKEN_URL, refreshRequest, { headers }).pipe(
      tap(response => {
        this.saveSession(response);
      })
    );
  }

  /**
   * Save session data to localStorage and update signals
   */
  private saveSession(response: LoginResponse): void {
    localStorage.setItem(this.STORAGE_KEY_USER, JSON.stringify(response));
    localStorage.setItem(this.STORAGE_KEY_TOKEN, response.accessToken.token);
    localStorage.setItem(this.STORAGE_KEY_REFRESH_TOKEN, response.refreshToken);
    
    this.currentUser.set(response);
    this.isAuthenticated.set(true);
  }

  /**
   * Load user data from localStorage
   */
  private loadUserFromStorage(): void {
    const userJson = localStorage.getItem(this.STORAGE_KEY_USER);
    if (userJson) {
      try {
        const user = JSON.parse(userJson) as LoginResponse;
        this.currentUser.set(user);
        this.isAuthenticated.set(true);
      } catch (error) {
        // Error loading user from storage, clearing session
        this.clearSession();
      }
    }
  }

  /**
   * Get the current access token
   */
  getAccessToken(): string | null {
    return localStorage.getItem(this.STORAGE_KEY_TOKEN);
  }

  /**
   * Get the current refresh token
   */
  getRefreshToken(): string | null {
    return localStorage.getItem(this.STORAGE_KEY_REFRESH_TOKEN);
  }

  /**
   * Clear session data (logout)
   */
  logout(): void {
    this.clearSession();
  }

  /**
   * Clear all session data from localStorage and signals
   */
  private clearSession(): void {
    localStorage.removeItem(this.STORAGE_KEY_USER);
    localStorage.removeItem(this.STORAGE_KEY_TOKEN);
    localStorage.removeItem(this.STORAGE_KEY_REFRESH_TOKEN);
    
    this.currentUser.set(null);
    this.isAuthenticated.set(false);
  }

  /**
   * Get current user data
   */
  getCurrentUser(): LoginResponse | null {
    return this.currentUser();
  }

  /**
   * Check if user is authenticated
   */
  checkAuthentication(): boolean {
    return this.isAuthenticated();
  }
}
